import { buildAmanahSection } from './amanahDocxTheme';
// Islamic Estate Planning Kit — bundles every blank template into a single
// ZIP for offline-friendly distribution (masjid workshops, financial-advisor
// hand-outs, family meetings, etc.).
//
// Contents of the ZIP (all blank, ready to print or fill digitally):
//   00-START-HERE.docx                        — cover & how-to letter
//   01-family-financial-budget.docx + .csv
//   02-zakat-worksheet.docx + .csv            (added 2026-03-07)
//   03-sadaqah-log.docx + .csv
//   04-waqf-deed-draft.docx
//   05-inheritance-faraid-worksheet.docx + .csv
//   06-islamic-will-wasiyyah.docx + .csv
//   07-end-of-life-checklist.docx
//   08-estate-readiness-checklist.docx
//   09-succession-planning-cover.docx
//   10-succession-planning-workbook.docx

import { downloadBudgetCsv, downloadBudgetDocx, downloadSadaqahCsv, downloadSadaqahDocx, downloadWaqfDocx, downloadEolDocx, downloadEstateDocx } from './lifeTemplates';
import { downloadInheritanceWorksheetDocx, downloadInheritanceWorksheetCsv } from './inheritanceTemplate';
import { downloadZakatWorksheetDocx, downloadZakatWorksheetCsv } from './zakatTemplate';
import { buildSuccessionCoverDocx, buildSuccessionWorkbookDocx } from './successionKitDocs';

const stamp = () => new Date().toISOString().slice(0, 10);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Cover page — a friendly "Start here" letter explaining the kit
const buildCoverDocx = async () => {
  const docx = await import('docx');
  const { Document, Packer, Paragraph, TextRun, AlignmentType } = docx;
  const h1 = (t) => new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 240 },
    children: [new TextRun({ text: t, bold: true, size: 36, color: '0F4C3A' })] });
  const h2 = (t) => new Paragraph({ spacing: { before: 280, after: 120 },
    children: [new TextRun({ text: t, bold: true, size: 28, color: '0F4C3A' })] });
  const p = (t, opts = {}) => new Paragraph({ spacing: { after: 120 },
    children: [new TextRun({ text: t, size: 22, ...opts })] });
  const li = (t) => new Paragraph({ spacing: { after: 60 }, bullet: { level: 0 },
    children: [new TextRun({ text: t, size: 22 })] });

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Amanah document',
    children: [
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 },
        children: [new TextRun({ text: 'بسم الله الرحمن الرحيم', size: 28 })] }),
      h1('Islamic Estate Planning Kit'),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 360 },
        children: [new TextRun({ text: 'A complete, printable, hand-fillable bundle.', italics: true, size: 22, color: '666666' })] }),

      p('Assalamu alaykum wa rahmatullahi wa barakatuh.'),
      p('This kit contains every form, worksheet and checklist you need to put your Islamic financial life in order — designed so that anyone in your family can pick it up, print it, and fill it by hand. Each file is blank by default. There is also a fully interactive version of every tool available, free, at zakat.international.'),

      h2('What\u2019s inside this folder'),
      li('00-START-HERE.docx — this letter.'),
      li('01-family-financial-budget.docx + .csv — monthly income/expenses + four Islamic savings pots (Zakat reserve, Hajj fund, Sadaqah jar, emergency fund).'),
      li('02-zakat-worksheet.docx + .csv — collect nisab basis, all zakatable assets, deductions, and the 8 Qur\u2019anic disbursement categories.'),
      li('03-sadaqah-log.docx + .csv — a private record of your charity, anonymous or named.'),
      li('04-waqf-deed-draft.docx — design a perpetual endowment (water, education, masjid, healthcare, etc.).'),
      li('05-inheritance-faraid-worksheet.docx + .csv — collect every piece of data a faraid scholar needs.'),
      li('06-islamic-will-wasiyyah.docx + .csv — the formal Wasiyyah with witness/executor blanks.'),
      li('07-end-of-life-checklist.docx — ghusl, kafan, janazah, burial, debts, executor, key documents.'),
      li('08-estate-readiness-checklist.docx — one-page tick-box overview of the seven items above.'),
      li('09-succession-planning-cover.docx — an executive letter your family / executor will open first.'),
      li('10-succession-planning-workbook.docx — a 12-section fill-by-hand workbook: executor, guardian, funeral wishes, estate inventory, digital accounts, notification list, first-30-days checklist.'),

      h2('Recommended order'),
      p('Start with the End-of-Life Checklist (07) — it usually surfaces the most surprising gaps. Fill Zakat (02) at your Hawl anniversary each Hijri year. Walk the Inheritance Worksheet (05) so you know your heir landscape. With those filled, the Will (06) becomes much easier. Budget (01) and Sadaqah (03) can be filled monthly. Waqf (04) is optional — but a powerful sadaqah jariyah.'),

      h2('Once you have filled them'),
      p('Schedule a meeting with: (a) a qualified Islamic scholar / mufti for the religious soundness of your Will and Faraid worksheet, and (b) a lawyer or notary in your country to register the Will and (if applicable) the Waqf deed. This kit prepares you for that meeting — it is not a substitute for it.'),

      h2('Privacy'),
      p('Every file in this kit was generated on your own device. Nothing was uploaded to any server. The interactive online version at zakat.international stores its data exclusively in your browser\u2019s localStorage — also never on a server.'),

      h2('A note on the in-app version'),
      p('When you fill the same data online at zakat.international, the in-app tools will (a) compute live totals (surplus, lifetime sadaqah, faraid shares) automatically, (b) cross-link your Will to your Inheritance to your Estate Readiness scorecard, and (c) generate a printable PDF you can hand to your executor. Use whichever feels right — paper, screen, or both.'),

      p('May Allah accept your efforts and bless your family.', { bold: true, italics: true }),
      p('— Amanah by Zakat.International'),

      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 320 },
        children: [new TextRun({ text: 'zakat.international  ·  free, open, scholarly  ·  ' + stamp(), size: 18, color: '888888' })] }),
    ],
  })] });

  return Packer.toBlob(doc);
};

// Tiny inline blank Will builder — separate from the heavy willDocx module
// (which depends on testator state). For a blank kit page we just need a
// printable scaffold; users who want the full interactive version visit
// /will online.
const buildBlankWillDocx = async () => {
  const docx = await import('docx');
  const { Document, Packer, Paragraph, TextRun, AlignmentType, UnderlineType } = docx;
  const blank = (w = 40) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' }, size: 22,
  });
  const h = (t) => new Paragraph({ spacing: { before: 240, after: 120 },
    children: [new TextRun({ text: t, bold: true, size: 26, color: '0F4C3A' })] });
  const p = (t, opts = {}) => new Paragraph({ spacing: { after: 120 },
    children: [new TextRun({ text: t, size: 22, ...opts })] });
  const f = (label) => new Paragraph({ spacing: { after: 160 },
    children: [new TextRun({ text: `${label}: `, bold: true, size: 22 }), blank(60)] });

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'ISLAMIC WILL (WASIYYAH) — BLANK TEMPLATE',
    children: [
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 80 },
        children: [new TextRun({ text: 'بسم الله الرحمن الرحيم', size: 28 })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 240 },
        children: [new TextRun({ text: 'ISLAMIC WILL (WASIYYAH) — BLANK TEMPLATE', bold: true, size: 32 })] }),
      p('This is a starting template. The interactive version at zakat.international/will produces a fully formatted Wasiyyah with the 1/3 cap auto-enforced and the faraid shares pre-calculated. This blank version is for handwriting / discussion with a scholar.', { italics: true, color: '666666' }),

      h('1 · Declaration'),
      p('I, the undersigned, being of sound mind and free will, do hereby declare this to be my last Islamic Will (Wasiyyah), executed in accordance with the Sharia.'),
      f('Full legal name'),
      f('Date of birth'),
      f('National ID / passport no.'),
      f('Address'),

      h('2 · Faith declaration (shahadah)'),
      p('I bear witness that there is no god but Allah, alone, with no partner, and I bear witness that Muhammad is His servant and messenger.'),

      h('3 · Debts'),
      p('Before any distribution, my debts to people and to Allah (unpaid Zakat) MUST be settled in full.'),
      f('Debts owed (creditor, amount, contact)'),
      f('Debts owed (creditor, amount, contact)'),
      f('Debts owed (creditor, amount, contact)'),

      h('4 · Wasiyyah / bequests (CAPPED at 1/3 of NET estate)'),
      p('Per the hadith of Saʿd b. Abi Waqqas (Bukhari 2742 / Muslim 1628), bequests may not exceed one-third of the estate, and bequests to Quranic heirs are not permitted (except with all heirs\u2019 consent).'),
      f('Beneficiary, amount, purpose'),
      f('Beneficiary, amount, purpose'),
      f('Beneficiary, amount, purpose'),

      h('5 · Faraid (Allah\u2019s inheritance shares)'),
      p('The remaining 2/3 (after debts and bequests) shall be distributed strictly per the Sharia rules of Faraid (Quran 4:11, 4:12, 4:176). See the Inheritance Worksheet (04-) in this kit for the data your scholar will use.'),

      h('6 · Executor (wasiy)'),
      f('Primary executor (full name)'),
      f('Executor contact'),
      f('Alternate executor (full name)'),
      f('Alternate executor contact'),

      h('7 · Guardian of minor children'),
      f('Primary guardian (full name)'),
      f('Alternate guardian (full name)'),

      h('8 · Funeral preferences'),
      p('(Detail in the End-of-Life Checklist 06-)'),

      h('9 · Witnesses'),
      p('This Wasiyyah must be signed before two adult Muslim witnesses (or as your jurisdiction\u2019s civil law requires).'),
      f('Witness 1 — name'),
      f('Witness 1 — signature & date'),
      f('Witness 2 — name'),
      f('Witness 2 — signature & date'),

      new Paragraph({ spacing: { before: 360 },
        children: [new TextRun({ text: 'Testator signature: ', bold: true, size: 22 }), blank(50)] }),
      new Paragraph({ spacing: { before: 100 },
        children: [new TextRun({ text: 'Date: ', bold: true, size: 22 }), blank(30)] }),
      new Paragraph({ spacing: { before: 200 },
        children: [new TextRun({ text: 'This blank template is not a legally executed Will until you have signed it before two adult Muslim witnesses and (where required) registered it with the competent civil authority in your jurisdiction.', italics: true, size: 18, color: '666666' })] }),
    ],
  })] });
  return Packer.toBlob(doc);
};

/**
 * Blank Wasiyyah — .csv companion for the DOCX above.
 * Same field order so scholars can key it into any Wasiyyah tool.
 */
const buildBlankWillCsv = () => {
  const rows = [
    ['Amanah Islamic Will (Wasiyyah) — Blank template', '', 'Generated', stamp()],
    [],
    ['1. Declaration'],
    ['Full legal name', ''],
    ['Date of birth', ''],
    ['National ID / passport no.', ''],
    ['Address', ''],
    [],
    ['2. Faith declaration (Shahadah)'],
    ['I bear witness that there is no god but Allah and that Muhammad is His messenger.', ''],
    [],
    ['3. Debts to be settled BEFORE any distribution', 'Creditor', 'Amount', 'Contact'],
    ['Debt 1', '', '', ''],
    ['Debt 2', '', '', ''],
    ['Debt 3', '', '', ''],
    [],
    ['4. Wasiyyah bequests (max 1/3 of net estate; not to Qur\'anic heirs)', 'Beneficiary', 'Amount', 'Purpose'],
    ['Bequest 1', '', '', ''],
    ['Bequest 2', '', '', ''],
    ['Bequest 3', '', '', ''],
    [],
    ['5. Faraid (remaining 2/3 distributed per Q 4:11, 4:12, 4:176)', 'See Inheritance Worksheet (04-)', ''],
    [],
    ['6. Executor (wasiy)'],
    ['Primary executor — full name', ''],
    ['Primary executor — contact', ''],
    ['Alternate executor — full name', ''],
    ['Alternate executor — contact', ''],
    [],
    ['7. Guardian of minor children'],
    ['Primary guardian', ''],
    ['Alternate guardian', ''],
    [],
    ['8. Funeral preferences', 'See End-of-Life Checklist (06-)'],
    [],
    ['9. Witnesses — two adult Muslim witnesses required', 'Name', 'Signature', 'Date'],
    ['Witness 1', '', '', ''],
    ['Witness 2', '', '', ''],
    [],
    ['Testator signature', ''],
    ['Date', ''],
  ];
  const esc = (v) => {
    const s = String(v ?? '');
    return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n') + '\n';
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
};

/**
 * Bundle every blank template into one ZIP.
 * Privacy-first: everything is generated and zipped IN the browser.
 * `onProgress(stepLabel, fractionDone)` is invoked at each milestone so the
 * caller can render a friendly progress UI (the kit takes ~6-10s to build).
 */
export const downloadEstatePlanningKit = async (onProgress = () => {}) => {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const steps = [
    ['Cover letter',          () => buildCoverDocx().then((b) => zip.file('00-START-HERE.docx', b))],
    ['Budget (Word)',         () => downloadBudgetDocx(null, { asBlob: true }).then((b) => zip.file('01-family-financial-budget.docx', b))],
    ['Budget (CSV)',          () => Promise.resolve(zip.file('01-family-financial-budget.csv', downloadBudgetCsv(null, { asBlob: true })))],
    ['Zakat (Word)',          () => downloadZakatWorksheetDocx(null, { asBlob: true }).then((b) => zip.file('02-zakat-worksheet.docx', b))],
    ['Zakat (CSV)',           () => Promise.resolve(zip.file('02-zakat-worksheet.csv', downloadZakatWorksheetCsv(null, { asBlob: true })))],
    ['Sadaqah log (Word)',    () => downloadSadaqahDocx(null, { asBlob: true }).then((b) => zip.file('03-sadaqah-log.docx', b))],
    ['Sadaqah log (CSV)',     () => Promise.resolve(zip.file('03-sadaqah-log.csv', downloadSadaqahCsv(null, { asBlob: true })))],
    ['Waqf deed',             () => downloadWaqfDocx(null, { asBlob: true }).then((b) => zip.file('04-waqf-deed-draft.docx', b))],
    ['Inheritance (Word)',    () => downloadInheritanceWorksheetDocx(null, { asBlob: true }).then((b) => zip.file('05-inheritance-faraid-worksheet.docx', b))],
    ['Inheritance (CSV)',     () => Promise.resolve(zip.file('05-inheritance-faraid-worksheet.csv', downloadInheritanceWorksheetCsv(null, { asBlob: true })))],
    ['Will (Word)',           () => buildBlankWillDocx().then((b) => zip.file('06-islamic-will-wasiyyah.docx', b))],
    ['Will (CSV)',            () => Promise.resolve(zip.file('06-islamic-will-wasiyyah.csv', buildBlankWillCsv()))],
    ['End-of-life checklist', () => downloadEolDocx(null, { asBlob: true }).then((b) => zip.file('07-end-of-life-checklist.docx', b))],
    ['Estate readiness',      () => downloadEstateDocx({}, { asBlob: true }).then((b) => zip.file('08-estate-readiness-checklist.docx', b))],
    ['Succession cover',      () => buildSuccessionCoverDocx({ asBlob: true }).then((b) => zip.file('09-succession-planning-cover.docx', b))],
    ['Succession workbook',   () => buildSuccessionWorkbookDocx({ asBlob: true }).then((b) => zip.file('10-succession-planning-workbook.docx', b))],
  ];

  for (let i = 0; i < steps.length; i++) {
    const [label, fn] = steps[i];
    onProgress(label, i / (steps.length + 1));
    await fn();
  }
  onProgress('Packing ZIP', steps.length / (steps.length + 1));
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
  downloadBlob(blob, `islamic-estate-planning-kit-${stamp()}.zip`);
  onProgress('Done', 1);
};
